import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import type { FlowRole } from '../shared/useFlows';

/**
 * The Library's newer shelves, as the client reads them.
 *
 * ITEMS (`g_libitems`, f15) are the vocabulary a flow step is picked from.
 * INSTRUCTION SHEETS (`g_libinstr`, f12b) are what the patient takes home.
 * The ORDER CATALOG (`g_libcat`, f14) gains the in-clinic switch, the price
 * that switch requires, and the dispatch that its absence implies.
 * A PROTOCOL (`g_libproto`, f13) gains a review state.
 *
 * Every response goes through the same unwrap: the backend wraps in
 * `{success, data}` and a paginated body is `{data, total}` inside that.
 */

const unwrap = <T,>(res: { data?: unknown }): T => {
  const d = res.data as { data?: unknown } | undefined;
  return ((d && typeof d === 'object' && 'data' in d ? d.data : d) ?? null) as T;
};

/* ---------------------------------------------------------------- items -- */

export type ItemProduces = 'nothing' | 'document' | 'values';

export interface FlowItem {
  id: string;
  name: string;
  assigneeRole: FlowRole;
  description: string | null;
  waitMin: number | null;
  needsItemId: string | null;
  produces: ItemProduces;
  consentRequired: boolean;
  billable: boolean;
  catalogItemId: string | null;
  /** What one run uses up — "1 × nebule (salbutamol)". */
  consumes?: ItemConsumption[] | null;
  /** A course: each sitting is its own numbered task. */
  repeats?: { count: number; everyDays: number } | null;
  isActive: boolean;
  usageCount: number;
  updatedAt: string;
}

export interface ItemConsumption { name: string; qty: number; stockItemId?: string | null }

export type ItemDraft = Omit<
  FlowItem,
  'id' | 'isActive' | 'usageCount' | 'updatedAt'
>;

export const itemsApi = {
  async list(): Promise<FlowItem[]> {
    return unwrap<FlowItem[]>(await apiClient.get('/flows/items')) ?? [];
  },
  async create(body: Partial<ItemDraft>) {
    return unwrap<FlowItem>(await apiClient.post('/flows/items', body));
  },
  async update(id: string, body: Partial<ItemDraft> & { isActive?: boolean }) {
    return unwrap<FlowItem>(await apiClient.patch(`/flows/items/${id}`, body));
  },
  async remove(id: string) {
    return unwrap<{ id: string }>(await apiClient.delete(`/flows/items/${id}`));
  },
};

export function useFlowItems() {
  const q = useQuery({
    queryKey: ['flows', 'items'],
    queryFn: itemsApi.list,
    staleTime: 60_000,
    retry: false,
  });
  return { ...q, data: q.data ?? [] };
}

/* --------------------------------------------------- instruction sheets -- */

export interface InstructionSheet {
  id: string;
  name: string;
  language: string;
  body: string;
  specialty: string | null;
  isSeeded: boolean;
  forkedFromId: string | null;
  sentCount: number;
  isActive: boolean;
  updatedAt: string;
}

export const sheetsApi = {
  async list(): Promise<InstructionSheet[]> {
    return unwrap<InstructionSheet[]>(await apiClient.get('/instruction-sheets')) ?? [];
  },
  async create(body: { name: string; language?: string; body?: string }) {
    return unwrap<InstructionSheet>(await apiClient.post('/instruction-sheets', body));
  },
  async duplicate(id: string) {
    return unwrap<InstructionSheet>(
      await apiClient.post(`/instruction-sheets/${id}/duplicate`, {}),
    );
  },
  async update(
    id: string,
    body: Partial<{ name: string; language: string; body: string; isActive: boolean }>,
  ) {
    return unwrap<InstructionSheet>(await apiClient.patch(`/instruction-sheets/${id}`, body));
  },
  async remove(id: string) {
    return unwrap<{ id: string }>(await apiClient.delete(`/instruction-sheets/${id}`));
  },
};

/** A sheet as given to one patient — filled, frozen, in their portal. */
export interface GivenSheet {
  id: string;
  title: string;
  language: string;
  body: string;
  givenAt: string;
}

export const givenSheetsApi = {
  async give(
    patientId: string,
    body: { sheetId: string; meds?: string[]; context?: 'prescription' | 'referral' | 'visit'; medicalRecordId?: string },
  ): Promise<GivenSheet> {
    return unwrap<GivenSheet>(await apiClient.post(`/patients/${patientId}/instruction-sheets`, body));
  },
};

export function useInstructionSheets() {
  const q = useQuery({
    queryKey: ['instruction-sheets'],
    queryFn: sheetsApi.list,
    staleTime: 60_000,
    retry: false,
  });
  return { ...q, data: q.data ?? [] };
}

/* -------------------------------------------------------- order catalog -- */

export type CatalogKind = 'lab' | 'imaging' | 'drug' | 'intervention';

export interface CatalogItem {
  id: string;
  tenantId: string | null;
  kind: CatalogKind;
  name: string;
  code: string | null;
  category: string | null;
  specimen: string | null;
  bodyPart: string | null;
  notes: string | null;
  source: 'seed' | 'manual' | 'auto';
  isActive: boolean;
  inClinic: boolean;
  price: string | null;
  defaultInstructions: string | null;
  externalProviderId: string | null;
}

export type CatalogDraft = Partial<
  Pick<
    CatalogItem,
    | 'kind'
    | 'name'
    | 'code'
    | 'category'
    | 'inClinic'
    | 'price'
    | 'defaultInstructions'
    | 'externalProviderId'
  >
>;

export const catalogApi = {
  /**
   * EVERY KIND, IN ONE READ. `/order-catalog` without a kind returns the whole
   * shelf — the four lanes are counted from it, so "All 61" and "Lab 24" can
   * never disagree with each other the way two separate reads would.
   */
  async list(): Promise<CatalogItem[]> {
    return unwrap<CatalogItem[]>(await apiClient.get('/order-catalog')) ?? [];
  },
  async create(body: CatalogDraft) {
    return unwrap<CatalogItem>(await apiClient.post('/order-catalog', body));
  },
  async update(id: string, body: CatalogDraft) {
    return unwrap<CatalogItem>(await apiClient.patch(`/order-catalog/${id}`, body));
  },
};

export function useCatalog() {
  const q = useQuery({
    queryKey: ['order-catalog', 'all'],
    queryFn: catalogApi.list,
    staleTime: 60_000,
    retry: false,
  });
  return { ...q, data: q.data ?? [] };
}

/* ------------------------------------------------------------ protocols -- */

export const protocolReviewApi = {
  async review(id: string) {
    return unwrap(await apiClient.post(`/protocols/${id}/review`, {}));
  },
  async submitForReview(id: string) {
    return unwrap(await apiClient.post(`/protocols/${id}/submit-review`, {}));
  },
};

/** One invalidator for every Library shelf — a save shows up everywhere. */
export function useLibraryRefresh() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['flows'] }),
      qc.invalidateQueries({ queryKey: ['instruction-sheets'] }),
      qc.invalidateQueries({ queryKey: ['order-catalog'] }),
      qc.invalidateQueries({ queryKey: ['protocols'] }),
    ]);
}

/** Saving anything in the Library, with the toast the page shows. */
export function useLibrarySave<TArgs, TOut>(fn: (args: TArgs) => Promise<TOut>) {
  const refresh = useLibraryRefresh();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void refresh(),
  });
}
